import type { NavKey } from '@shared-core/navigation/routes'
import { useSyncExternalStore } from 'react'

// Стек открытых нижних формшитов (web-презентация). На mobile не используется
// (там формшиты — экраны react-navigation). Живёт в core, т.к. навигационные
// хуки кросс-платформенные и тоже в core.
// key: NavKey, а не SheetKey — web показывает шторкой и некоторые экраны,
// которые на mobile зарегистрированы с options.popup (например phones info/delete).
export type SheetInstance = { id: string; key: NavKey; params: unknown; closing: boolean }

let stack: SheetInstance[] = []
const listeners = new Set<() => void>()
let counter = 0
let suppressNextPopstateClose = false

function emit() {
  for (const listener of listeners) listener()
}

// После pop() + navigate() goBack() уже вызвал history.back(); следующий popstate
// не должен закрывать только что открытый шит (Safari строже к порядку событий).
export const sheetNavGuard = {
  suppressNextPopstateClose() {
    suppressNextPopstateClose = true
  },

  consumePopstateSuppress(): boolean {
    if (!suppressNextPopstateClose) return false
    suppressNextPopstateClose = false
    return true
  },
}

export const sheetStore = {
  getSnapshot: () => stack,

  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  open(key: NavKey, params: unknown) {
    stack = [...stack, { id: `sheet_${++counter}`, key, params, closing: false }]
    // Пустая запись истории (тот же URL) — чтобы браузерный «Назад» закрывал шит.
    // Снимается через history.back() при любом закрытии (см. SheetHost/goBack).
    window.history.pushState(window.history.state, '')
    emit()
  },

  // Пометить верхний открытый шит на закрытие (анимация выезда; удалит remove()).
  closeTop() {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (!stack[i].closing) {
        stack = stack.map((item, index) => (index === i ? { ...item, closing: true } : item))
        emit()
        return
      }
    }
  },

  remove(id: string) {
    stack = stack.filter((item) => item.id !== id)
    emit()
  },

  // Сброс «зависших» closing-шитов (например, если анимация не доиграла после смены темы).
  clearClosing() {
    if (!stack.some((item) => item.closing)) return
    stack = stack.filter((item) => !item.closing)
    emit()
  },

  hasOpen() {
    return stack.some((item) => !item.closing)
  },

  // Шит помечен closing и доигрывает анимацию — в стеке ещё есть, но hasOpen() уже false.
  isAnimating() {
    return stack.length > 0 && !this.hasOpen()
  },

  // Ключ верхнего открытого шита (для getCurrentRouteName).
  topKey(): NavKey | undefined {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (!stack[i].closing) return stack[i].key
    }
    return undefined
  },

  // Полностью очистить стек (для popTo / reset / popToTop на вебе).
  reset() {
    if (stack.length === 0) return
    stack = []
    emit()
  },

  // Заменить верхний открытый шит без новой записи в history.
  replaceTopInPlace(key: NavKey, params: unknown) {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (!stack[i].closing) {
        stack = stack.map((item, index) =>
          index === i ? { id: `sheet_${++counter}`, key, params, closing: false } : item,
        )
        emit()
        return
      }
    }

    this.open(key, params)
  },

  // После pop(): убрать closing-шиты и открыть новый (pushState снаружи).
  replaceAfterClose(key: NavKey, params: unknown) {
    stack = stack.filter((item) => !item.closing)
    stack = [...stack, { id: `sheet_${++counter}`, key, params, closing: false }]
    emit()
  },
}

export function useSheetStack() {
  return useSyncExternalStore(sheetStore.subscribe, sheetStore.getSnapshot, sheetStore.getSnapshot)
}
